import {
  closestCenter,
  defaultDropAnimationSideEffects,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors,
  type Announcements,
  type CollisionDetection,
  type DragEndEvent,
  type DragMoveEvent,
  type DragStartEvent,
  type DropAnimation,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { GripVertical } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { resolveDrop, type DragSource } from '../../model/drop-rules';
import { cardTitle } from '../../model/card-title';
import { useWorkspace, useWorkspaceActions } from '../../state/workspace-context';
import { CardDragOverlay } from '../canvas/CardDragOverlay';
import {
  CardDragSessionContext,
  type CardDragSession,
} from '../canvas/card-drag-session';
import {
  placeCardFromDrag,
  measureCardPixels,
  pointerFromDelta,
  snapCardAtPointer,
} from '../canvas/place-card-from-drag';
import { ActiveDragContext, readDragSource, readDropTarget } from './drag-state';

/**
 * Con puntero: la zona mas especifica (menor area) bajo el cursor, para que una tarjeta
 * gane al canvas que la contiene. Con teclado (sin coordenadas): la zona mas cercana.
 */
const collisionDetection: CollisionDetection = (args) => {
  if (args.pointerCoordinates === null) return closestCenter(args);
  const area = (id: string | number): number => {
    const rect = args.droppableRects.get(id);
    return rect ? rect.width * rect.height : Number.POSITIVE_INFINITY;
  };
  return [...pointerWithin(args)].sort((a, b) => area(a.id) - area(b.id));
};

const announcements: Announcements = {
  onDragStart: () => 'Arrastre iniciado. Use las flechas para moverse y Espacio para soltar.',
  onDragOver: ({ over }) =>
    over ? 'Sobre una zona de destino.' : 'Fuera de las zonas de destino.',
  onDragEnd: ({ over }) => (over ? 'Elemento soltado.' : 'Arrastre cancelado.'),
  onDragCancel: () => 'Arrastre cancelado.',
};

const cardDropAnimation: DropAnimation = {
  duration: 240,
  easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
  sideEffects: defaultDropAnimationSideEffects({
    styles: {
      active: { opacity: '0.2' },
    },
  }),
};

/** Contexto de arrastrar y soltar del workspace: traduce cada drop en un comando. */
export function WorkspaceDndProvider({ children }: { readonly children: ReactNode }) {
  const { document, catalog } = useWorkspace();
  const { execute, dispatch } = useWorkspaceActions();
  const [active, setActive] = useState<DragSource | null>(null);
  const [cardSession, setCardSession] = useState<CardDragSession | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const clearCardSession = () => {
    setCardSession(null);
  };

  const onDragStart = (event: DragStartEvent) => {
    const source = readDragSource(event.active.data.current);
    setActive(source);
    if (source?.type !== 'card') {
      clearCardSession();
      return;
    }
    const card = document.cards.find((entry) => entry.id === source.cardId);
    if (!card) {
      clearCardSession();
      return;
    }
    const size = measureCardPixels(source.cardId, card);
    setCardSession({
      cardId: source.cardId,
      title: cardTitle(card, catalog),
      widthPx: size.widthPx,
      heightPx: size.heightPx,
      snap: null,
    });
  };

  const onDragMove = (event: DragMoveEvent) => {
    const source = readDragSource(event.active.data.current);
    if (source?.type !== 'card') return;
    const pointer = pointerFromDelta(event.activatorEvent, event.delta);
    if (pointer === null) return;
    const snap = snapCardAtPointer(pointer, source.cardId, document);
    setCardSession((current) => {
      if (current?.cardId !== source.cardId) return current;
      if (
        current.snap?.x === snap?.x &&
        current.snap?.y === snap?.y &&
        current.snap?.w === snap?.w &&
        current.snap?.h === snap?.h
      ) {
        return current;
      }
      return { ...current, snap };
    });
  };

  const onDragEnd = (event: DragEndEvent) => {
    setActive(null);
    clearCardSession();
    const source = readDragSource(event.active.data.current);
    const target = readDropTarget(event.over?.data.current);
    if (!source || !target) return;
    if (source.type === 'slot-field' && target.type === 'slot' && target.index === source.index) {
      return;
    }
    if (source.type === 'card') {
      if (target.type === 'canvas' || target.type === 'card' || target.type === 'card-position') {
        const place = placeCardFromDrag(event, source.cardId, document);
        if (place !== null) execute(place);
        return;
      }
    }
    if (source.type === 'card' && target.type !== 'canvas' && 'cardId' in target) {
      if (target.cardId === source.cardId) return;
    }
    const resolution = resolveDrop(source, target, document, catalog);
    if (resolution.ok) execute(resolution.command);
    else dispatch({ type: 'report-rejection', rejection: resolution.rejection });
  };

  const fieldOverlayLabel = (() => {
    if (!active || active.type === 'card') return null;
    if (active.type === 'field') return catalog.get(active.fieldId)?.name ?? active.fieldId;
    const card = document.cards.find((candidate) => candidate.id === active.cardId);
    const list = active.kind === 'dimension' ? card?.dimensions : card?.measures;
    const item = list?.[active.index];
    if (!item) return null;
    const fieldId = 'dimensionId' in item ? item.dimensionId : item.measureId;
    return catalog.get(fieldId)?.name ?? item.key;
  })();

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={onDragStart}
      onDragMove={onDragMove}
      onDragEnd={onDragEnd}
      onDragCancel={() => {
        setActive(null);
        clearCardSession();
      }}
      accessibility={{
        announcements,
        screenReaderInstructions: {
          draggable:
            'Pulse Espacio o Enter para arrastrar. Use las flechas para moverse, Espacio para soltar y Escape para cancelar.',
        },
      }}
    >
      <ActiveDragContext value={active}>
        <CardDragSessionContext value={cardSession}>{children}</CardDragSessionContext>
      </ActiveDragContext>
      <DragOverlay dropAnimation={cardSession !== null ? cardDropAnimation : null}>
        {cardSession !== null ? (
          <CardDragOverlay session={cardSession} />
        ) : fieldOverlayLabel === null ? null : (
          <div className="inline-flex items-center gap-1.5 rounded-md border border-primary/40 bg-popover px-2.5 py-1.5 text-xs font-medium shadow-raised">
            <GripVertical aria-hidden className="size-3.5 text-muted-foreground" />
            {fieldOverlayLabel}
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}
